/**
 * CalendarHeader
 *
 * Header do calendário no padrão do Design System (agenda.html):
 * - Toolbar superior via <PageToolbar> shared (F23: token header, 56px):
 *   esquerda = toggle do painel + título · centro = busca (ToolbarSearch) ·
 *   direita = Operadores → Filtros → Novo evento (ordem canônica F15).
 * - Subbar (below): esquerda = Hoje + navegação (‹ ›) + período · direita =
 *   seletor de visão (Mês / Semana / Dia / Lista) como segmented control.
 * Renderizado apenas no desktop (≥ lg) — o mobile usa a calbar própria da página.
 */

import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CalendarView } from '../types';
import { STATUSES, EVENT_TYPES } from './CalendarSidebar';
import type { CalendarFilters } from './CalendarSidebar';
import {
  PageToolbar,
  ToolbarSearch,
  FiltersButton,
  Button,
  OperatorAvatarFilter,
  type OperatorAvatarFilterOperator,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

interface CalendarHeaderProps {
  // Navigation
  currentDate: Date;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;

  // View
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;

  // User Filter
  users: OperatorAvatarFilterOperator[];
  selectedUserId: string;
  onUserChange: (userId: string) => void;

  // Actions
  onNewEvent: () => void;

  // Search (client-side sobre eventos já carregados)
  searchQuery: string;
  onSearchChange: (value: string) => void;

  // Sidebar
  onToggleSidebar: () => void;
  /** Mantido por compat de API — o header só renderiza ≥ lg (mobile usa a calbar da página). */
  onToggleMobileSidebar?: () => void;

  // Filtros (Tipo de evento + Status) — sincronizados com a sidebar
  filters: CalendarFilters;
  onFiltersChange: (filters: CalendarFilters) => void;

  // Scope-based visibility
  showOperatorFilter?: boolean;
}

// Segmented control de visões — ícones fiéis ao mockup (agenda.html): 'columns'
// para a visão de semana (#i-columns); demais mantêm grid/square/list.
const VIEW_OPTIONS: { value: CalendarView; label: string; icon: IconName }[] = [
  { value: 'month', label: 'Mês', icon: 'grid' },
  { value: 'week', label: 'Semana', icon: 'columns' },
  { value: 'day', label: 'Dia', icon: 'square' },
  { value: 'agenda', label: 'Lista', icon: 'list' },
];

export const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  onPrevious,
  onNext,
  onToday,
  view,
  onViewChange,
  users,
  selectedUserId,
  onUserChange,
  onNewEvent,
  searchQuery,
  onSearchChange,
  onToggleSidebar,
  filters,
  onFiltersChange,
  showOperatorFilter = true,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersWrapRef = useRef<HTMLSpanElement>(null);
  const [filtersPos, setFiltersPos] = useState({ top: 0, left: 0 });

  const toggleStatus = (statusId: string) => {
    const next = filters.statuses.includes(statusId)
      ? filters.statuses.filter((s) => s !== statusId)
      : [...filters.statuses, statusId];
    onFiltersChange({ ...filters, statuses: next });
  };

  const toggleType = (typeId: string) => {
    const next = filters.types.includes(typeId)
      ? filters.types.filter((t) => t !== typeId)
      : [...filters.types, typeId];
    onFiltersChange({ ...filters, types: next });
  };

  const activeFilterCount = filters.statuses.length + filters.types.length;

  const formatCurrentPeriod = () => {
    switch (view) {
      case 'month':
        return currentDate.toLocaleDateString('pt-BR', {
          month: 'long',
          year: 'numeric',
        });
      case 'week': {
        const weekStart = new Date(currentDate);
        const day = weekStart.getDay();
        weekStart.setDate(weekStart.getDate() - day);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return `${weekStart.getDate()} - ${weekEnd.getDate()} de ${weekStart.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}`;
      }
      case 'day':
        return currentDate.toLocaleDateString('pt-BR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      case 'agenda':
        return 'Próximos eventos';
      default:
        return '';
    }
  };

  return (
    <div className="flex-shrink-0" data-testid="calendar-header">
      {/* ===== Toolbar superior — PageToolbar shared (F23: header token, 56px) ===== */}
      <PageToolbar
        data-testid="calendar-toolbar"
        left={
          <>
            {/* Toggle painel (desktop) */}
            <button
              onClick={onToggleSidebar}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors hover:bg-[var(--theme-button-ghost-hover)] flex-none"
              style={{ color: 'var(--theme-muted-text)' }}
              title="Mostrar/ocultar painel"
              data-testid="calendar-panel-toggle"
            >
              <Icon name="panel-left" className="w-5 h-5" />
            </button>
            <span
              className="text-base font-bold tracking-tight whitespace-nowrap"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Agenda
            </span>
          </>
        }
        center={
          <ToolbarSearch
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Buscar eventos, participantes…"
            shrinkClass="ds-search hidden sm:flex"
            data-testid="calendar-search-input"
          />
        }
        right={
          <>
            {/* Ordem canônica F15: Operadores → Filtros → CTA primário */}
            {showOperatorFilter && (
              <OperatorAvatarFilter
                operators={users}
                selectedId={selectedUserId === 'all' ? null : selectedUserId}
                onSelect={(id) => onUserChange(id ?? 'all')}
                data-testid="calendar-operators"
              />
            )}

            {/* Botão Filtros canônico (F05) — badge de contagem inline */}
            <span ref={filtersWrapRef} className="flex-none inline-flex">
              <FiltersButton
                count={activeFilterCount}
                active={filtersOpen}
                onClick={() => {
                  if (!filtersOpen && filtersWrapRef.current) {
                    const rect = filtersWrapRef.current.getBoundingClientRect();
                    setFiltersPos({ top: rect.bottom + 8, left: rect.right });
                  }
                  setFiltersOpen((v) => !v);
                }}
                data-testid="calendar-filters-button"
              />
            </span>

            <Button
              variant="primary"
              icon="plus"
              collapsibleLabel
              onClick={onNewEvent}
              data-testid="calendar-new-event-button"
            >
              Novo evento
            </Button>

            {/* Popover de filtros — dropdown claro fixo (padrão dos dropdowns) */}
            {filtersOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[100]" onClick={() => setFiltersOpen(false)} />
                <div
                  style={{
                    position: 'fixed',
                    top: filtersPos.top,
                    left: filtersPos.left,
                    transform: 'translateX(-100%)',
                    zIndex: 100,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    padding: 8,
                    minWidth: 240,
                    maxHeight: 360,
                    overflowY: 'auto',
                  }}
                  data-testid="calendar-filters-popover"
                >
                  <div className="px-2 pt-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: '#94a3b8' }}>
                    Tipo de evento
                  </div>
                  {EVENT_TYPES.map((type) => {
                    const on = filters.types.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => toggleType(type.id)}
                        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm transition-colors"
                        style={{ backgroundColor: on ? '#f5f3ff' : 'transparent', color: on ? '#6d28d9' : '#334155' }}
                        data-testid={`calendar-filter-pop-type-${type.id}`}
                      >
                        <Icon name={type.icon} className="h-4 w-4 flex-shrink-0" style={{ color: on ? '#8b5cf6' : '#94a3b8' }} />
                        <span className="flex-1 truncate">{type.name}</span>
                        {on && <Icon name="check" className="h-4 w-4 flex-shrink-0" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                  <div className="my-1 border-t" style={{ borderColor: '#f1f5f9' }} />
                  <div className="px-2 pt-1 pb-1.5 text-[11px] font-bold uppercase tracking-wide" style={{ color: '#94a3b8' }}>
                    Status
                  </div>
                  {STATUSES.map((status) => {
                    const on = filters.statuses.includes(status.id);
                    return (
                      <button
                        key={status.id}
                        onClick={() => toggleStatus(status.id)}
                        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md text-left text-sm transition-colors"
                        style={{ backgroundColor: on ? '#f5f3ff' : 'transparent', color: on ? '#6d28d9' : '#334155' }}
                        data-testid={`calendar-filter-pop-status-${status.id}`}
                      >
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: status.color }} />
                        <span className="flex-1 truncate">{status.name}</span>
                        {on && <Icon name="check" className="h-4 w-4 flex-shrink-0" style={{ color: '#8b5cf6' }} />}
                      </button>
                    );
                  })}
                </div>
              </>,
              document.body
            )}
          </>
        }
        below={
          /* ===== Subbar — navegação + seletor de visão (mesma casca do header).
             `@container`: o slot below fica FORA do grid do PageToolbar, então a
             subbar precisa ser o próprio container das legendas (padrão DS 2xl:@tb). ===== */
          <div
            className="@container flex items-center gap-2 px-3 sm:px-4 flex-wrap"
            style={{
              minHeight: 48,
              backgroundColor: 'var(--theme-header-background)',
              borderBottom: '1px solid var(--theme-header-border)',
            }}
          >
            {/* Esquerda: Hoje + prev/next + período */}
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <button
                onClick={onToday}
                className="h-9 px-3.5 rounded-lg border font-semibold text-sm transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
                style={{ borderColor: 'var(--theme-input-border)', color: 'var(--theme-card-text)' }}
                data-testid="calendar-today-button"
              >
                Hoje
              </button>

              <div className="inline-flex gap-0.5">
                <button
                  onClick={onPrevious}
                  className="inline-flex items-center justify-center w-8 h-9 rounded-lg transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
                  style={{ color: 'var(--theme-muted-text)' }}
                  title="Anterior"
                  data-testid="calendar-prev-button"
                >
                  <Icon name="chevron-left" className="w-4 h-4" />
                </button>
                <button
                  onClick={onNext}
                  className="inline-flex items-center justify-center w-8 h-9 rounded-lg transition-colors hover:bg-[var(--theme-button-ghost-hover)]"
                  style={{ color: 'var(--theme-muted-text)' }}
                  title="Próximo"
                  data-testid="calendar-next-button"
                >
                  <Icon name="chevron-right" className="w-4 h-4" />
                </button>
              </div>

              <div
                className="text-base font-bold tracking-tight truncate capitalize"
                style={{ color: 'var(--theme-card-text)' }}
                data-testid="calendar-period-title"
              >
                {formatCurrentPeriod()}
              </div>
            </div>

            {/* Direita: segmented control de visões */}
            <div
              className="inline-flex rounded-lg p-0.5 gap-0.5 flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-input-background)' }}
              data-testid="calendar-view-switcher"
            >
              {VIEW_OPTIONS.map((opt) => {
                const active = view === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => onViewChange(opt.value)}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-sm font-semibold transition-colors"
                    style={{
                      backgroundColor: active ? 'var(--theme-card-background)' : 'transparent',
                      color: active ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                      border: active ? '1px solid var(--theme-card-border)' : '1px solid transparent',
                    }}
                    title={opt.label}
                    data-testid={`calendar-view-${opt.value}`}
                  >
                    <Icon name={opt.icon} className="w-4 h-4" />
                    <span className="hidden 2xl:@tb:inline">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        }
      />
    </div>
  );
};

CalendarHeader.displayName = 'CalendarHeader';
